/**
 * shared/lib/guardian-verification/attempt-binding.ts
 *
 * Binds ONE guardian mutual-challenge submission to ONE server-issued,
 * single-use nonce (issue #3029).
 *
 * ── The defect this closes ───────────────────────────────────────────────────
 *
 * Until this module existed, the ceremony's wire value WAS the stored secret:
 * the client sent `answer_hash_shake256` verbatim, byte-identical every time,
 * and byte-identical for BOTH sides of the same relationship (the requester's
 * triple for a relationship equalled the triple the guardian had to send).
 * Nothing bound a submission to a handshake, a hold, a leg, an actor or an
 * instant, so anyone who observed or exfiltrated one triple could replay it
 * until the underlying answers were re-enrolled. The per-attempt caps bounded
 * GUESSING; they did nothing about REPLAY. AC-87 says "arbitrary nonempty
 * certificate hashes and a signed-in session are insufficient" — a replayable
 * bearer digest is that same defect class.
 *
 * ── What is sent instead ─────────────────────────────────────────────────────
 *
 *     proof = HMAC-SHA256(key = nonce_utf8, msg = `${order}:${digest}`)   → 64 hex
 *
 * The digest itself never leaves the device any more. `digest` is still
 * `SHAKE256(salt_32B + canon(answer)_utf8, 512b)` computed exactly as Rule 1 /
 * L0-16 mandate — THIS MODULE DOES NOT TOUCH THAT FORMULA and must never be
 * edited to. It only wraps the result for one attempt.
 *
 * The `nonce` is minted server-side per attempt under Immutable Rule 17 (32
 * CSPRNG bytes → URL-safe base64, stored only as HMAC-SHA256(plaintext)) and
 * consumed exactly once, in a context tag naming the LEG, the ACTOR and the
 * SUBJECT (`server/logic/guardian/mutual-challenge-nonce.ts`). So:
 *
 *   - a captured proof is inert the moment the ceremony scores it (the nonce is
 *     spent, and the consume is a guarded `WHERE consumed_at IS NULL` update);
 *   - a requester's proof cannot be resubmitted as the guardian's, because the
 *     guardian's nonce lives under a different context tag and the requester's
 *     is already spent;
 *   - one guardian's proof cannot be replayed against another ceremony or
 *     another hold, for the same reason.
 *
 * The `order` prefix stops a proof for question 1 being resubmitted as the
 * answer to question 2 within the same attempt.
 *
 * ── The honest residual (Friction Rule clause 2) ─────────────────────────────
 *
 * This makes the WIRE non-replayable. It does NOT protect against an attacker
 * who holds the raw answers, or who compromises the device that computes the
 * digest — such an attacker mints their own nonce and answers correctly. What
 * the ceremony proves is unchanged: both parties held the enrolled shared
 * knowledge, and held their account, at the moment they answered. Never
 * who was at the keyboard, never presence.
 *
 * Lives in `shared/` because the client computes the proof and the server
 * recomputes it over the stored digest, and the two must be byte-identical by
 * construction rather than by two copies that can drift — the same reason
 * `./normalize.ts` and `../bespoke-seed/hash.ts` live here. `@noble/hashes` is
 * already an allowlisted external on both sides of this boundary (Rule 18); the
 * `/hmac` submodule is used server-side by `server/crypto.ts` today.
 */

import { hmac } from "@noble/hashes/hmac";
import { sha256 } from "@noble/hashes/sha256";

import { bytesToHex } from "../bespoke-seed/hash";

/**
 * A minted attempt nonce on the wire: `mintToken`'s 32 CSPRNG bytes as URL-safe
 * base64 without padding — exactly the shape `verifyAndConsume` accepts
 * (`server/lib/singleUseTokens.ts`), so a value this regex rejects could never
 * have been consumed anyway.
 */
export const MUTUAL_CHALLENGE_NONCE_RE = /^[A-Za-z0-9_-]{43}$/;

/**
 * One bound answer proof: HMAC-SHA256, 64 lowercase hex.
 *
 * DELIBERATELY NOT the 128-hex digest shape this wire used to carry. The
 * narrower shape is what makes an accidental regression visible: a client (or a
 * test) that goes back to sending the raw `answer_hash_shake256` is refused by
 * the edge schema for being the wrong length, rather than silently reinstating
 * the replayable bearer secret.
 */
export const MUTUAL_CHALLENGE_PROOF_RE = /^[0-9a-f]{64}$/;

/** How many questions one relationship's triple carries. */
export const MUTUAL_CHALLENGE_TRIPLE_LENGTH = 3;

/**
 * Bind ONE answer digest to ONE attempt nonce.
 *
 * THE ONLY BINDING ENTRY POINT, deliberately. A `bindAnswerTriple` batch form
 * used to sit beside this one with zero production callers — the client
 * (`client/src/lib/guardian-mutual-challenge.ts`) and the server
 * (`connectionAnswersMatch`) both bind one answer at a time — so the only code
 * exercising it was test fixtures, which meant the fixtures were not walking
 * the path production walks and a future caller could have been invited onto an
 * untested one. It was deleted rather than kept: callers that want a triple map
 * over this, exactly as production does.
 *
 * @param nonce   the server-issued single-use attempt nonce, verbatim
 * @param order   the question's 1-based order within its own triple
 * @param answerHashShake256  128-hex SHAKE256-512 digest (Rule 1 / L0-16)
 */
export function bindAnswerDigest(
  nonce: string,
  order: number,
  answerHashShake256: string,
): string {
  const key = new TextEncoder().encode(nonce);
  const message = new TextEncoder().encode(`${order}:${answerHashShake256}`);
  return bytesToHex(hmac(sha256, key, message));
}
